import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import authService from '../../../../services/auth.service';
import { FiCopy, FiExternalLink, FiGlobe, FiInfo } from 'react-icons/fi';
import SectionHelpModal from '../../../../components/common/SectionHelpModal';
import { useLocation } from 'react-router-dom';
import AppTour from '../../../../components/common/AppTour';

const SelfServiceLink = () => {
    const { user } = useAuth();
    const [localCompanySlug, setLocalCompanySlug] = useState(null);
    const location = useLocation();
    const [runTour, setRunTour] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'autoatencion') {
            setRunTour(true);
        }
    }, [location.search]);

    useEffect(() => {
        const fetchUserData = async () => {
            // Si el usuario del contexto ya tiene el slug, usarlo
            if (user?.empresa_slug || user?.empresa?.slug_autoatencion) {
                setLocalCompanySlug(user.empresa_slug || user.empresa.slug_autoatencion);
                return;
            }

            // Si no, intentar refescar los datos del usuario desde el backend
            try {
                const updatedUser = await authService.getCurrentUser();
                if (updatedUser?.empresa_slug) {
                    setLocalCompanySlug(updatedUser.empresa_slug);
                }
            } catch (error) {
                console.error("Error refreshing user data", error);
            }
        };
        fetchUserData();
    }, [user]);

    // Fallback inteligente
    const slugToUse = localCompanySlug || user?.empresa_slug || user?.empresa?.slug_autoatencion || 'mi-empresa';
    const publicUrl = `${window.location.origin}/autoatencion/${slugToUse}`;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(publicUrl);
        alert('¡Enlace copiado al portapapeles!');
    };

    const autoatencionTourSteps = [
        {
            element: '#tour-autoatencion-header',
            intro: 'Tu Catálogo Online: ¡Este es un súper beneficio! El sistema crea una página web automática para que tus clientes vean tus productos y armen su carrito de compras solos.',
            position: 'bottom',
        },
        {
            element: '#tour-autoatencion-link',
            intro: 'Tu Enlace Único: Este link es tuyo y de nadie más. Compártelo en tus redes sociales o envíalo por WhatsApp para empezar a vender por internet.',
            position: 'top',
        },
        {
            element: '#tour-autoatencion-botones',
            intro: 'Acciones Rápidas: Copia el link con un clic o dale a "Abrir" para ver exactamente cómo lo verán tus clientes.',
            position: 'top',
        },
        {
            // Sin element para que se muestre en el centro como modal
            intro: '¡Felicidades, llegaste al final! Has completado el recorrido por IdevPlus. Ya tienes todo listo para ordenar tu negocio y vender mejor. ¡Te deseamos muchísimo éxito!',
        }
    ];

    return (
        <div className="container-custom" style={{ overflow: 'hidden' }}>
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={autoatencionTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Finalizar' }}
                    onTourComplete={() => {
                        window.location.href = '/';
                    }}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '20px', borderBottom: 'none' }} id="tour-autoatencion-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h2 style={{ marginBottom: 0 }}>Link de Autoatención</h2>
                        <SectionHelpModal
                            title="¿Qué es el Link de Autoatención?"
                            content={
                                <div>
                                    <p>Este es tu portal exclusivo para clientes. Funciona como una vitrina o catálogo virtual conectado en tiempo real con IdevPlus.</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Autoservicio:</strong> Si lo compartes en tus redes sociales o WhatsApp, los clientes podrán entrar a ver tus productos, agregar lo que necesitan al carrito, y enviarte una solicitud formal de cotización.</li>
                                        <li><strong>Bandeja de Entrada:</strong> Esas solicitudes autogeneradas llegarán directamente a tu sección de "Cotizaciones" (en estado Borrador), listas para que tú solo las revises, apliques descuentos y presiones "Terminar y Enviar".</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p className="text-muted" style={{ marginTop: '5px' }}>Comparte este enlace con tus clientes para que soliciten cotizaciones</p>
                </div>
            </div>

            <div className="card-custom" style={{ textAlign: 'center', padding: '40px 16px' }}>
                <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--orange-light)',
                    color: 'var(--primary-orange)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px auto'
                }}>
                    <FiGlobe size={40} />
                </div>

                <h3 style={{ marginBottom: '10px' }}>Tu Portal de Cotizaciones 24/7</h3>
                <p className="text-muted" style={{ maxWidth: '600px', margin: '0 auto 30px auto', wordWrap: 'break-word' }}>
                    Tus clientes pueden ingresar a este enlace para ver tu catálogo (si lo habilitas) y solicitar cotizaciones directamente. Las solicitudes aparecerán en tu panel como "Borrador".
                </p>

                <div className="alert-custom alert-info" style={{ maxWidth: '600px', margin: '0 auto 30px auto', textAlign: 'left', display: 'flex', gap: '10px' }}>
                    <FiInfo size={20} style={{ minWidth: '20px' }} />
                    <span>
                        <strong>Tip:</strong> Puedes personalizar el mensaje de bienvenida y las notificaciones que reciben tus clientes en la sección de <a href="/configuracion" style={{ color: 'inherit', textDecoration: 'underline' }}>Configuración de Empresa</a>.
                    </span>
                </div>

                <div className="selfservice-link-actions">
                    <div style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: 'var(--gray-100)',
                        borderRadius: '8px',
                        border: '1px solid var(--gray-300)',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        color: 'var(--text-dark)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} id="tour-autoatencion-link">
                        {publicUrl}
                    </div>

                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }} id="tour-autoatencion-botones">
                        <button className="btn-custom btn-primary" onClick={copyToClipboard}>
                            <FiCopy size={18} style={{ marginRight: '5px' }} /> Copiar
                        </button>

                        <a
                            href={publicUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-custom"
                            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', background: '#f8f9fa', color: '#333', border: '1px solid #ddd' }}
                        >
                            <FiExternalLink size={18} style={{ marginRight: '5px' }} /> Abrir
                        </a>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default SelfServiceLink;
